import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Check, ArrowRight, Sparkles, Activity, Shield, Zap, LogOut, Tag, Video, Infinity as InfIcon, Film, Star, Users
} from "lucide-react";
import { toast } from "sonner";
import { LogoIcon } from "@/components/Wordmark";

// Shared benefits — Starter includes everything the app has been doing.
const STARTER_FEATURES = [
  { text: "Unlimited trade imports (all broker CSVs)", pro: false },
  { text: "Deep performance analytics + equity curves", pro: false },
  { text: "Interactive calendar + daily recap", pro: false },
  { text: "Coach AI — Claude Sonnet 4.5", pro: false, limit: "3 messages / day" },
  { text: "Trade screenshots & tags", pro: false },
  { text: "Streak tracking + morning briefings", pro: false },
];

// Pro-only killer features
const PRO_FEATURES = [
  { text: "Everything in Starter", icon: Check },
  { text: "Video Trade Replay — auto-cuts screen recordings into per-trade clips", icon: Video, badge: "NEW" },
  { text: "Unlimited Coach AI messages", icon: InfIcon },
  { text: "Public share links on any clip (great for coaching or posting)", icon: Users },
  { text: "20 GB video storage · starred clips kept forever", icon: Film },
  { text: "Early access to every future feature", icon: Star },
];

const TIER_CARDS = ({ billing, tier, onCheckout, loading, isCurrent }) => {
  const isPro = tier === "pro";
  const price = isPro ? billing?.plan_price_pro : billing?.plan_price_starter;
  return { isPro, price };
};

export default function Pricing() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const [loadingTier, setLoadingTier] = useState(null);
  const [showCode, setShowCode] = useState(false);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [restoring, setRestoring] = useState(false);

  // Partner/influencer code — captured from either the URL (?ref=ROSS20) OR
  // localStorage (set on any earlier landing) so it survives auth redirects.
  const [partnerCode, setPartnerCode] = useState(() => {
    if (typeof window === "undefined") return null;
    const url = new URLSearchParams(window.location.search).get("ref");
    if (url) {
      const c = url.trim().toUpperCase();
      window.localStorage.setItem("tg_partner_ref", c);
      return c;
    }
    return window.localStorage.getItem("tg_partner_ref");
  });

  const { data: partnerInfo } = useQuery({
    queryKey: ["partner-lookup", partnerCode],
    queryFn: async () => partnerCode ? (await api.get(`/partner/lookup?code=${encodeURIComponent(partnerCode)}`)).data : null,
    enabled: !!partnerCode,
    retry: false,
  });

  const { data: billing } = useQuery({
    queryKey: ["billing-status"],
    queryFn: async () => (await api.get("/billing/status")).data,
  });

  const currentTier = billing?.plan_tier || "starter";
  const isSubscribed = !!billing?.is_subscribed;
  const isTrialing = !!billing?.is_trialing;
  const hasStripeCustomer = !!billing?.has_stripe_customer;
  // Double-subscription guard: if we've seen this customer in Stripe before
  // (customer id on file, or an active/past-due subscription id) but our local
  // is_subscribed flag says "no", the safe move is portal — NOT a fresh checkout.
  // Otherwise Stripe would happily bill them twice.
  const doubleSubRisk = hasStripeCustomer && !isSubscribed && !!billing?.stripe_subscription_id;
  const [confirmCheckout, setConfirmCheckout] = useState(null); // { tier } | null

  const startCheckout = async (tier) => {
    // Guard: user has a Stripe customer + subscription on file but local status
    // says "not subscribed" (webhook lag, past_due, or canceled-with-history).
    // Route them to the portal instead of creating a second sub.
    if (doubleSubRisk) {
      setConfirmCheckout({ tier });
      return;
    }
    // If user is already subscribed to Starter and wants Pro, use prorated upgrade
    // instead of creating a second subscription.
    if (isSubscribed && currentTier === "starter" && tier === "pro") {
      setLoadingTier(tier);
      try {
        const r = await api.post("/billing/upgrade-to-pro");
        toast.success("Upgraded to Pro. Stripe will prorate the difference on your next invoice — you won't be double-charged.");
        qc.invalidateQueries({ queryKey: ["billing-status"] });
        setLoadingTier(null);
        return;
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Upgrade failed");
        setLoadingTier(null);
        return;
      }
    }
    setLoadingTier(tier);
    try {
      const r = await api.post("/billing/checkout", {
        origin_url: window.location.origin,
        tier,
        partner_code: partnerInfo ? partnerInfo.code : null, // only send if VALID
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setLoadingTier(null);
    }
  };

  const openPortal = async () => {
    try {
      const r = await api.post("/billing/portal", { return_url: `${window.location.origin}/pricing` });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portal unavailable — make a purchase first");
    }
  };

  const restoreAccess = async () => {
    setRestoring(true);
    try {
      const r = await api.post("/billing/reconcile");
      qc.invalidateQueries({ queryKey: ["billing-status"] });
      if (r.data?.billing?.has_access) toast.success("Access restored from Stripe.");
      else toast("No active subscription found on Stripe under your email.", { icon: "🔍" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setRestoring(false); }
  };

  // Silent auto-reconcile on page load
  const didAutoReconcile = useRef(false);
  useEffect(() => {
    if (didAutoReconcile.current || !billing) return;
    if (billing.subscriptions_enabled && billing.status !== "active" && billing.status !== "trialing") {
      didAutoReconcile.current = true;
      (async () => {
        try {
          const r = await api.post("/billing/reconcile");
          if (r.data?.billing?.has_access) {
            qc.invalidateQueries({ queryKey: ["billing-status"] });
            toast.success("Welcome back — access restored from Stripe.");
          }
        } catch { /* silent */ }
      })();
    }
  }, [billing, qc]);

  const redeem = async () => {
    if (!code.trim()) return;
    setRedeeming(true);
    try {
      const r = await api.post("/billing/redeem", { code: code.trim(), origin_url: window.location.origin });
      if (r.data.applied) {
        toast.success(`${r.data.label.toUpperCase()} · Access granted`);
        qc.invalidateQueries({ queryKey: ["billing-status"] });
        setShowCode(false); setCode("");
        setTimeout(() => navigate("/"), 1500);
      } else if (r.data.url) {
        toast.success(`${r.data.label.toUpperCase()} · Redirecting to checkout for $${r.data.discounted_amount}`);
        window.location.href = r.data.url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally { setRedeeming(false); }
  };

  const proPrice = billing?.plan_price_pro ?? 29.99;
  const starterPrice = billing?.plan_price_starter ?? 19.99;

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: "var(--bg)", color: "var(--fg)", fontFamily: "var(--font-body)" }}>
      {/* Aurora glow */}
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 900px 500px at 8% 5%, rgba(34,197,94,0.08), transparent 60%),
            radial-gradient(ellipse 900px 500px at 92% 105%, rgba(139,92,246,0.12), transparent 60%),
            radial-gradient(ellipse 1200px 700px at 50% 50%, rgba(139,92,246,0.03), transparent 70%)
          `,
        }}
      />
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none opacity-[0.025]"
        style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,1) 1px, transparent 1px)", backgroundSize: "24px 24px" }}
      />

      {/* Header */}
      <header className="relative border-b px-8 py-5 flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")} data-testid="pricing-brand">
          <LogoIcon size={22} variant={currentTier === "pro" ? "purple" : "green"} />
          <span className="font-heading text-lg font-semibold tracking-tight">
            TRADE<span style={{ color: currentTier === "pro" ? "var(--accent)" : "var(--green)" }}>GREEN</span>
          </span>
        </div>
        <div className="flex items-center gap-4">
          {user && (
            <>
              <span className="text-xs font-mono-num" style={{ color: "var(--fg-3)" }}>{user.email}</span>
              <button onClick={logout} data-testid="pricing-logout" className="text-xs hover:text-rose-400 flex items-center gap-1.5" style={{ color: "var(--fg-3)" }}>
                <LogOut className="h-3 w-3" strokeWidth={1.5} /> SIGN OUT
              </button>
            </>
          )}
        </div>
      </header>

      <div className="relative max-w-6xl mx-auto px-8 py-16">
        <div className="text-center mb-12">
          <div className="label-eyebrow" style={{ color: "var(--accent)" }}>PRICING · 7 DAYS FREE</div>
          <h1 className="font-heading text-5xl md:text-6xl font-semibold mt-3 tracking-tight">
            The trader&apos;s <span style={{ color: "var(--accent)" }}>edge</span>, replayed.
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-base leading-relaxed" style={{ color: "var(--fg-2)" }}>
            The trading journal that auto-cuts your screen recordings into a video clip for every single trade.
            Study your own tape like a game film. Nobody else does this.
          </p>
        </div>

        {/* Billing status pill */}
        {billing && (
          <div className="flex justify-center mb-10">
            <div
              className="px-4 py-2 border text-xs font-mono-num flex items-center gap-3"
              style={{
                borderColor:
                  billing.status === "active" ? "rgba(34,197,94,0.35)" :
                  billing.status === "trialing" ? "rgba(168,85,247,0.35)" : "var(--border)",
                background:
                  billing.status === "active" ? "rgba(34,197,94,0.08)" :
                  billing.status === "trialing" ? "rgba(168,85,247,0.08)" : "var(--surface)",
                color:
                  billing.status === "active" ? "var(--green)" :
                  billing.status === "trialing" ? "var(--accent)" : "var(--fg-2)",
                borderRadius: "999px",
              }}
              data-testid="billing-status-pill"
            >
              <Shield className="h-3.5 w-3.5" strokeWidth={1.5} />
              {billing.status === "active" && <>SUBSCRIBED · {(currentTier || "STARTER").toUpperCase()} · {billing.days_left_sub} DAYS LEFT</>}
              {billing.status === "trialing" && <>FREE TRIAL · {billing.days_left_trial} DAYS LEFT</>}
              {billing.status === "expired" && <>NO ACTIVE SUBSCRIPTION</>}
            </div>
          </div>
        )}

        {/* Partner code applied banner — shown when user landed via ?ref=CODE */}
        {partnerInfo && (
          <div
            className="max-w-2xl mx-auto mb-6 p-4 flex items-center gap-3"
            style={{
              background: "rgba(34,197,94,0.06)",
              border: "1px solid rgba(34,197,94,0.35)",
              borderRadius: "12px",
            }}
            data-testid="partner-code-banner"
          >
            <span className="text-lg">🎉</span>
            <div className="flex-1 text-sm" style={{ color: "var(--fg)" }}>
              <strong style={{ color: "var(--green)" }}>{partnerInfo.discount_pct}% off for {partnerInfo.discount_months} months</strong>{" "}
              applied by <strong>{partnerInfo.name}</strong>{" "}
              <span className="font-mono-num text-xs" style={{ color: "var(--fg-3)" }}>· code {partnerInfo.code}</span>
            </div>
          </div>
        )}

        {/* Two-tier grid */}
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {/* STARTER */}
          <PlanCard
            tier="starter"
            title="Starter"
            price={starterPrice}
            tagline="For traders getting serious about their edge."
            description="The full TradeGreen journal — imports, analytics, calendar, AI Coach (capped at 3 messages a day). Great for daily reviews and building a track record."
            features={STARTER_FEATURES}
            isCurrent={isSubscribed && currentTier === "starter"}
            isTrialing={isTrialing}
            ctaLabel={
              isSubscribed && currentTier === "starter" ? "CURRENT PLAN"
              : isSubscribed && currentTier === "pro" ? "DOWNGRADE VIA BILLING PORTAL"
              : isTrialing ? "SUBSCRIBE — LOCK IN STARTER"
              : "START 7-DAY FREE TRIAL"
            }
            ctaDisabled={isSubscribed && currentTier === "starter"}
            ctaSecondary={isSubscribed && currentTier === "pro"}
            onCheckout={() => (isSubscribed && currentTier === "pro" ? openPortal() : startCheckout("starter"))}
            loading={loadingTier === "starter"}
          />
          {/* PRO */}
          <PlanCard
            tier="pro"
            title="Pro"
            price={proPrice}
            tagline="For traders who study their tape like game film."
            description="Everything in Starter, plus the killer feature nobody else has: upload your OBS recording and get a bite-sized clip auto-cut for every trade — instantly reviewable, taggable, and shareable. Plus unlimited Coach AI."
            features={PRO_FEATURES}
            isFeatured
            isCurrent={isSubscribed && currentTier === "pro"}
            isTrialing={isTrialing}
            ctaLabel={
              isSubscribed && currentTier === "pro" ? "CURRENT PLAN"
              : isSubscribed && currentTier === "starter" ? "UPGRADE TO PRO · PRORATED"
              : isTrialing ? "SUBSCRIBE — GO PRO"
              : "START 7-DAY FREE TRIAL · PRO"
            }
            ctaSubtext={
              isSubscribed && currentTier === "starter"
                ? "You'll be credited for unused Starter days. The prorated Pro difference appears on your NEXT invoice — no immediate second charge."
                : null
            }
            ctaDisabled={isSubscribed && currentTier === "pro"}
            onCheckout={() => startCheckout("pro")}
            loading={loadingTier === "pro"}
          />
        </div>

        {/* Restore + billing management + promo — kept below the grid */}
        <div className="max-w-md mx-auto mt-10 space-y-3">
          {!isSubscribed && !hasStripeCustomer && (
            <button
              onClick={restoreAccess}
              disabled={restoring}
              data-testid="restore-access-btn"
              className="w-full py-2.5 text-xs tracking-wide flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
              style={{ background: "transparent", border: "1px solid rgba(34,197,94,0.4)", color: "var(--green)", borderRadius: "10px" }}>
              {restoring ? "CHECKING STRIPE…" : "ALREADY PAID? RESTORE ACCESS"}
            </button>
          )}
          {(isSubscribed || hasStripeCustomer) && (
            <button onClick={openPortal} data-testid="portal-btn"
              className="w-full py-2.5 text-xs tracking-wide flex items-center justify-center gap-2 transition-colors"
              style={{ background: "var(--surface)", border: "1px solid var(--border)", color: "var(--fg-2)", borderRadius: "10px" }}>
              MANAGE BILLING · VIEW RECEIPTS · CANCEL
            </button>
          )}
          {/* Promo code */}
          <div className="pt-3">
            {!showCode ? (
              <button onClick={() => setShowCode(true)} data-testid="open-promo-btn"
                className="mx-auto flex items-center gap-1.5 text-xs transition-colors" style={{ color: "var(--fg-3)" }}>
                <Tag className="h-3 w-3" strokeWidth={1.5} /> HAVE A PROMO CODE?
              </button>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text" value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === "Enter") redeem(); }}
                  placeholder="ENTER CODE"
                  data-testid="promo-input"
                  className="flex-1 px-3 py-2 text-xs focus:outline-none font-mono-num tracking-widest"
                  style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--fg)", borderRadius: "8px" }}
                />
                <button onClick={redeem} disabled={!code.trim() || redeeming} data-testid="redeem-btn"
                  className="px-4 py-2 text-xs disabled:opacity-50 transition-colors"
                  style={{ background: "var(--accent)", color: "white", borderRadius: "8px" }}>
                  {redeeming ? "…" : "APPLY"}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Trust row */}
        <div className="mt-16 grid md:grid-cols-3 gap-6 text-center">
          {[
            { icon: Video, title: "Video replay — first of its kind", desc: "Auto-cuts every trade from your OBS recording. See exactly what you were looking at when you clicked." },
            { icon: Shield, title: "Cancel anytime", desc: "No long contracts. You stay because it makes you money." },
            { icon: Sparkles, title: "AI Coach built in", desc: "Claude Sonnet 4.5 auto-analyzes your edge every day. Attach video clips (Pro) for tape-level critiques." },
          ].map((b, i) => (
            <div key={i} className="p-5" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "14px", backdropFilter: "blur(12px)" }}>
              <b.icon className="h-5 w-5 mx-auto" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
              <div className="font-heading text-sm mt-3" style={{ color: "var(--fg)" }}>{b.title}</div>
              <div className="text-xs mt-2 leading-relaxed" style={{ color: "var(--fg-3)" }}>{b.desc}</div>
            </div>
          ))}
        </div>

        {/* Stripe badge */}
        <div className="mt-10 flex items-center justify-center gap-2 text-[10px] font-mono-num tracking-[0.18em] select-none" style={{ color: "var(--fg-3)" }} data-testid="stripe-badge">
          <Shield className="h-3 w-3" strokeWidth={1.5} style={{ color: "rgba(34,197,94,0.7)" }} />
          <span>POWERED BY</span>
          <span className="font-semibold" style={{ color: "var(--fg-2)", fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>stripe</span>
          <span>·</span><span>SECURE CHECKOUT · PCI-DSS</span>
          {billing?.stripe_mode && (
            <span
              data-testid="stripe-mode-badge"
              className="ml-2 px-1.5 py-0.5 border text-[9px] tracking-wider"
              style={
                billing.stripe_mode === "live"
                  ? { borderColor: "rgba(34,197,94,0.4)", background: "rgba(34,197,94,0.1)", color: "var(--green)" }
                  : { borderColor: "rgba(245,158,11,0.4)", background: "rgba(245,158,11,0.1)", color: "#fbbf24" }
              }
            >
              {billing.stripe_mode === "live" ? "LIVE" : "TEST MODE"}
            </span>
          )}
        </div>
      </div>

      {/* Double-subscription confirmation modal */}
      {confirmCheckout && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.75)" }}
          onClick={() => setConfirmCheckout(null)}
          data-testid="double-sub-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="max-w-md w-full p-6"
            style={{ background: "var(--bg)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: "14px" }}
          >
            <div className="label-eyebrow mb-2" style={{ color: "#fbbf24" }}>⚠︎ ALREADY A CUSTOMER</div>
            <h3 className="font-heading text-xl mb-3">You have a subscription on file</h3>
            <p className="text-sm mb-4" style={{ color: "var(--fg-2)" }}>
              Our records show you already have a Stripe subscription (status: <strong>{billing?.subscription_status || "past_due"}</strong>).
              Starting a new checkout would create a <strong>second charge</strong>. Instead, use the billing portal to update your payment method, change plans, or reactivate.
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={async () => { setConfirmCheckout(null); await openPortal(); }}
                data-testid="double-sub-open-portal"
                className="w-full py-3 text-sm font-semibold"
                style={{ background: "var(--accent)", color: "white", borderRadius: "10px" }}
              >
                OPEN BILLING PORTAL
              </button>
              <button
                onClick={() => setConfirmCheckout(null)}
                data-testid="double-sub-cancel"
                className="w-full py-2.5 text-xs"
                style={{ color: "var(--fg-3)" }}
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------

const PlanCard = ({ tier, title, price, tagline, description, features, isFeatured, isCurrent, isTrialing, ctaLabel, ctaSubtext, ctaDisabled, ctaSecondary, onCheckout, loading }) => (
  <div
    className="relative p-8 transition-all"
    data-testid={`plan-${tier}`}
    style={{
      background: isFeatured
        ? "linear-gradient(180deg, rgba(168,85,247,0.06), var(--surface) 60%)"
        : "var(--surface)",
      border: isFeatured ? "1px solid rgba(168,85,247,0.35)" : "1px solid var(--border)",
      borderRadius: "16px",
      backdropFilter: "blur(12px)",
      boxShadow: isFeatured ? "0 0 40px rgba(168,85,247,0.08)" : "none",
    }}
  >
    {isFeatured && (
      <div
        className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] font-mono-num tracking-widest z-10"
        style={{ background: "var(--accent)", color: "white", borderRadius: "6px", boxShadow: "0 4px 20px rgba(168,85,247,0.4)" }}
      >
        MOST POWERFUL
      </div>
    )}
    {isCurrent && (
      <div
        className="absolute top-4 right-4 px-2 py-1 text-[10px] font-mono-num tracking-widest"
        style={{ background: "rgba(34,197,94,0.15)", color: "var(--green)", borderRadius: "6px", border: "1px solid rgba(34,197,94,0.35)" }}
      >
        CURRENT
      </div>
    )}
    <div className="label-eyebrow mb-2 flex items-center gap-2" style={{ color: isFeatured ? "var(--accent)" : "var(--fg-3)" }}>
      {isFeatured ? <Zap className="h-3 w-3" strokeWidth={1.5} /> : <Shield className="h-3 w-3" strokeWidth={1.5} />}
      {tier.toUpperCase()}
    </div>
    <h3 className="font-heading text-3xl font-semibold mt-2 tracking-tight flex items-center gap-2.5">
      <LogoIcon size={26} variant={tier === "pro" ? "purple" : "green"} />
      TradeGreen {title}
    </h3>
    <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--fg-2)" }}>{tagline}</p>

    <div className="flex items-baseline gap-2 mt-6">
      <span className="font-mono-num text-5xl md:text-6xl font-semibold" style={{ color: "var(--fg)", letterSpacing: "-0.03em" }}>${price}</span>
      <span className="text-sm font-mono-num" style={{ color: "var(--fg-3)" }}>/ MONTH</span>
    </div>
    <div className="text-[11px] font-mono-num mt-1" style={{ color: "var(--fg-3)" }}>7 DAYS FREE · CANCEL ANY TIME</div>

    <p className="mt-5 text-sm leading-relaxed" style={{ color: "var(--fg-2)" }}>{description}</p>

    <ul className="mt-6 space-y-2.5">
      {features.map((f, i) => {
        const Icon = f.icon || Check;
        const isNew = f.badge === "NEW";
        return (
          <li key={i} className="flex items-start gap-3 text-sm" style={{ color: "var(--fg-2)" }}>
            <Icon className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={2} style={{ color: isFeatured ? "var(--accent)" : "var(--green)" }} />
            <span className="flex-1">
              {f.text}
              {f.limit && <span className="ml-2 text-[11px] font-mono-num" style={{ color: "var(--fg-3)" }}>· {f.limit}</span>}
              {isNew && <span className="ml-2 text-[10px] font-mono-num px-1.5 py-0.5" style={{ background: "var(--accent)", color: "white", borderRadius: "4px" }}>NEW</span>}
            </span>
          </li>
        );
      })}
    </ul>

    <button
      onClick={onCheckout}
      disabled={ctaDisabled || loading}
      data-testid={`checkout-btn-${tier}`}
      className="w-full mt-8 py-3.5 text-sm font-semibold tracking-wide flex items-center justify-center gap-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
      style={{
        background: ctaSecondary ? "transparent" : (isFeatured ? "var(--accent)" : "var(--green)"),
        color: ctaSecondary ? "var(--fg-2)" : "white",
        border: ctaSecondary ? "1px solid var(--border)" : "none",
        borderRadius: "10px",
        boxShadow: !ctaSecondary && !ctaDisabled ? (isFeatured ? "0 4px 24px rgba(168,85,247,0.35)" : "0 4px 24px rgba(34,197,94,0.25)") : "none",
      }}
    >
      {loading ? "REDIRECTING TO STRIPE…" : ctaLabel}
      {!loading && !ctaDisabled && <ArrowRight className="h-4 w-4" strokeWidth={2} />}
    </button>

    {!ctaDisabled && !ctaSecondary && !loading && !isTrialing && (
      <div className="mt-3 text-center text-[11px] font-mono-num tracking-wider" style={{ color: isFeatured ? "rgba(168,85,247,0.85)" : "rgba(34,197,94,0.8)" }}>
        CANCEL ANYTIME · 0 CHARGES DURING TRIAL
      </div>
    )}
    {ctaSubtext && (
      <div className="mt-3 text-center text-[11px] leading-relaxed px-2" style={{ color: "var(--fg-3)" }} data-testid={`plan-${tier}-cta-subtext`}>
        {ctaSubtext}
      </div>
    )}
  </div>
);
